import { GroupFilter } from './GroupFilter/GroupFilter';
import { POSButton } from '../POSButton/POSButton';
import React, { useCallback, useEffect, useState } from 'react';
import { FilterGroup, FilterGroupItem } from '../../interfaces/POSGroupFilter';

import './POSGroupFilter.less';
import { SELECT_ALL } from './POSGroupFilterConstants';
import { adjustGroupId } from '../../utils/GroupFilterHelper';

export interface Props {
  filterGroups: FilterGroup[];
  filterItems?: FilterGroupItem[];
  defaultSelectedItems?: FilterGroupItem[];
  selectedTabIndex?: number;
  tabPaneLoadingText?: string;
  onTabChange?: (filterGroupId: string, activeIndex: number) => void;
  onFilterChange?: (filterItems: FilterGroupItem[]) => void;
  onApplyFilters?: (filterItems: FilterGroupItem[]) => void;
  onReset?: () => void;
}

export const POSGroupFilter = ({
  filterGroups,
  filterItems,
  defaultSelectedItems,
  selectedTabIndex = 0,
  tabPaneLoadingText,
  onTabChange,
  onFilterChange,
  onApplyFilters,
  onReset,
}: Props) => {
  const [selectedFilterItems, setSelectedFilterItems] = useState<
    FilterGroupItem[]
  >(defaultSelectedItems ?? []);
  const [activeTabIndex, setActiveTabIndex] = useState(selectedTabIndex);
  const [selectedGroupId, setSelectedGroupId] = useState(
    filterGroups[selectedTabIndex].groupId
  );

  const handleApplyFilters = useCallback(() => {
    onApplyFilters?.([...selectedFilterItems]);
  }, [onApplyFilters, selectedFilterItems]);

  const handleReset = useCallback(() => {
    setSelectedFilterItems([]);
    setActiveTabIndex(0);
    onReset?.();
  }, [onReset]);

  const handleFilterChange = useCallback(
    (filterItem: FilterGroupItem, isChecked: boolean) => {
      if (filterItem.itemId === SELECT_ALL) {
        const filters = [...(filterItems ?? [])];
        const currentGroupItems = filters.filter(
          (item) => adjustGroupId(item.groupId) === selectedGroupId
        );

        let selectAllItems: FilterGroupItem[] = [];

        isChecked
          ? setSelectedFilterItems((prev) => {
              selectAllItems = [...new Set([...prev, ...currentGroupItems])];
              return selectAllItems;
            })
          : setSelectedFilterItems((prev) => {
              const selectedItems = prev.filter(
                (p) =>
                  !currentGroupItems.map((c) => c.itemId).includes(p.itemId)
              );
              selectAllItems = [...selectedItems];
              return selectAllItems;
            });
        onFilterChange?.(selectAllItems);
        return;
      }

      const selectedItems = [...selectedFilterItems];
      if (
        isChecked &&
        selectedItems.findIndex((item) => item.itemId === filterItem.itemId) < 0
      ) {
        selectedItems.push(filterItem);
      } else {
        selectedItems.splice(
          selectedFilterItems.findIndex((x) => x.itemId === filterItem.itemId),
          1
        );
      }
      setSelectedFilterItems(selectedItems);
      onFilterChange?.(selectedItems);
    },
    [filterItems, onFilterChange, selectedFilterItems, selectedGroupId]
  );

  const handleTabChange = useCallback(
    (groupId: string, activeIndex: any) => {
      setActiveTabIndex(activeIndex);
      setSelectedGroupId(groupId);
      onTabChange?.(groupId, activeIndex);
    },
    [onTabChange]
  );

  useEffect(() => {
    if (defaultSelectedItems) {
      setSelectedFilterItems(defaultSelectedItems);
    }
  }, [defaultSelectedItems]);

  return (
    <>
      <GroupFilter
        filterGroups={filterGroups}
        filterItems={filterItems}
        selectedFilterItems={selectedFilterItems}
        activeTabIndex={activeTabIndex}
        tabPaneLoadingText={tabPaneLoadingText}
        onTabChange={handleTabChange}
        onFilterChange={handleFilterChange}
      />
      <hr />
      <POSButton inverted floated="left" onClick={handleReset}>
        RESET FILTERS
      </POSButton>
      <POSButton floated="right" onClick={handleApplyFilters}>
        APPLY FILTERS
      </POSButton>
    </>
  );
};
